import * as React from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import {
    ActionButton,
    AlertDialog,
    AlertDialogContent,
    Badge,
    Button,
    Card,
    Dialog,
    DialogContent,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FormCheckbox,
    MoreButton,
    Tooltip,
} from "@jhool-io/fe-components";

import {
    useFetchClientBasicAndPersonalInfo,
    useFetchClientCreditCards,
} from "../../../../../hooks/queries/client";
import useCreditCardInput from "../../../../../hooks/useCreditCard";
import { copyToClipboard } from "../../../../../utils/helpers/copy";
import AddCardDetails from "../AddCardDetails/AddCardDetails";
import { UserPermisions } from "../../../../../utils/types/user";
import { useDoesUserHavePermission } from "../../../../../hooks/permissions";
import CardTransactionHistory from "./CardTransactionHistory/CardTransactionHistory";
import useToast from "../../../../../hooks/useToast";
import AddCardLabelForm from "./AddCardLabelForm/AddCardLabelForm";
import ListState from "../../../../../components/ListState/ListState";
import {
    useDeleteCreditCards,
    useSetPreferredCard,
    useValidateCard,
} from "../../../hooks/clients.mutations";
import {
    ClientCardTypes,
    PaymentCardStatus,
} from "../../../types/clients.types";
import InfoIcon from "../../../../../icons/Info";
import CopyIcon from "../../../../../icons/Copy";
import Loader from "../../../../../shared-ui/Loader/Loader";
import EditIcon from "../../../../../icons/Edit";
import DeleteIcon from "../../../../../icons/Delete";

type Dialogs =
    | "add_card"
    | "add_label"
    | "transaction_history"
    | "delete_card"
    | "delete_cards";

export default function CreditCards() {
    // Component local states
    const [searchParams, setSearchParams] = useSearchParams();
    const [cardPaymentID, setCardPaymentID] = React.useState("");
    const [isIDCopied, setIsIDCopied] = React.useState(false);
    React.useState(false);
    const [cardToDelete, setCardToDelete] = React.useState<string | null>(null);
    const [dialogInView, setDialogInView] = React.useState<Dialogs | null>(
        null
    );
    const [cardIdsToDelete, setCardIdsToDelete] = React.useState<string[]>([]);

    // Get id from url params
    const params = useParams();
    const clientId = params.clientId as string;

    // Query client
    const queryClient = useQueryClient();

    // Fetch client credit cards
    const { data, isLoading, error, isSuccess } =
        useFetchClientCreditCards(clientId);

    const deleteCards = useDeleteCreditCards(clientId);

    const clientPersonalInfo = useFetchClientBasicAndPersonalInfo(clientId);

    const isMutating = useIsMutating(["add-card", clientId]);

    const isAddLabelMutating = useIsMutating(["add-card-label", cardPaymentID]);

    const { cardImages } = useCreditCardInput();

    const validateCard = useValidateCard();

    const setPreferredCard = useSetPreferredCard();

    const { toast } = useToast();

    const canManageCreditCards = useDoesUserHavePermission([
        UserPermisions.CLIENT_INFO_PAYMENTS_WRITE,
        UserPermisions.CLIENT_INFO_ALL_ADMIN,
    ]);

    const defaultFormValue = data?.data.find(
        (card) => card.payment_card_id === cardPaymentID
    )?.card_label;
    // fetch client merchant Id

    // check if logged in user can add payment cards
    const canAddCard = useDoesUserHavePermission([
        UserPermisions.CLIENT_INFO_ALL_ADMIN,
        UserPermisions.CLIENT_INFO_ALL_WRITE,
        UserPermisions.CLIENT_INFO_PAYMENTS_WRITE,
        UserPermisions.CLIENT_INFO_FEES_WRITE,
    ]);

    const handleCardToValidate = (id: string) => {
        validateCard.mutate(
            {
                paymentCardId: id,
                clientId,
            },
            {
                onSuccess(res) {
                    queryClient.invalidateQueries({
                        queryKey: [clientId, `cards`],
                    });
                    toast({
                        mode: "success",
                        message: res.message || "Card validated successfully",
                    });
                },
                onError(err) {
                    toast({
                        mode: "error",
                        message:
                            err.response?.data.message ||
                            "Could not validate card at this time, please try again",
                    });
                },
            }
        );
    };

    const handleSetPreferredCard = (id: string, isPreferred: boolean) => {
        setPreferredCard.mutate(
            {
                paymentCardId: id,
                clientId,
                isPreferred,
            },
            {
                onSuccess() {
                    queryClient.invalidateQueries({
                        queryKey: [clientId, `cards`],
                    });
                    toast({
                        mode: "success",
                        message: isPreferred
                            ? "Card set as current"
                            : "Current card reset to automatic",
                    });
                },
                onError(err) {
                    toast({
                        mode: "error",
                        message:
                            err.response?.data.message ||
                            "Could not update the current card at this time, please try again",
                    });
                },
            }
        );
    };

    const handleDeleteCard = () => {
        const cardsIdsForPayload =
            dialogInView === "delete_cards"
                ? cardIdsToDelete
                : [cardToDelete as string];

        deleteCards.mutate(
            {
                card_ids: cardsIdsForPayload,
                clientId,
            },
            {
                onSuccess(res) {
                    queryClient.invalidateQueries({
                        queryKey: [clientId, `cards`],
                    });
                    toast({
                        mode: "success",
                        message: res.message || "Card(s) deleted successfully",
                    });
                    setDialogInView(null);
                    setCardToDelete(null);
                    setCardIdsToDelete([]);
                },
                onError(err) {
                    toast({
                        mode: "error",
                        message:
                            err?.response?.data?.message ||
                            "Could not delete card(s) at this time, please try again",
                    });
                },
            }
        );
    };

    // Function to determine credit card provider icon to display
    const getCreditCardIconToDisplay = (cardType: string) => {
        switch (cardType) {
            case ClientCardTypes.AMERICAN_EXPRESS:
                return cardImages.amex;

            case ClientCardTypes.MASTERCARD:
                return cardImages.mastercard;

            case ClientCardTypes.DISCOVER_FINANCIAL:
                return cardImages.discover;

            case ClientCardTypes.CHASE:
                return cardImages.chase;

            case ClientCardTypes.JCB:
                return cardImages.jcb;

            case ClientCardTypes.CAPITAL_ONE:
                return cardImages.capitalone;

            case ClientCardTypes.VISA:
                return cardImages.visa;

            case ClientCardTypes.DINERSCLUB:
                return cardImages.dinersclub;

            default:
                return cardImages.capitalone;
        }
    };

    const listHeader = (
        <div className="py-[--padding-card-y]">
            <div className="flex justify-between gap-[--spacing-items] flex-wrap">
                <div className="flex items-center gap-x-[--spacing-items] max-[768px]:flex-col max-[768px]:items-start">
                    <h3 className="text-base font-bold max-[768px]:pb-8">
                        Credit Card Info
                    </h3>
                    <div className="flex items-center max-[768px]:flex-col max-[768px]:items-start">
                        <p className="text-base font-medium max-[768px]:pb-8">
                            {" "}
                            Authorize.net Customer ID -{" "}
                        </p>

                        <Tooltip content="Use this ID to link the Customer profile on Authorize.net with the Client chart on Mantle, click to learn more">
                            <div className="flex gap-x-2 items-center">
                                <a
                                    className="text-textLink font-medium pl-[4px] max-[768px]:pl-0 underline"
                                    href="https://docs.google.com/document/d/16Sl2vNO7U4Mzk8HGCbN1mDf--awHw0vT_pkxNBcfr3U/edit?usp=sharing"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    {" "}
                                    {
                                        clientPersonalInfo?.data?.data
                                            ?.merchant_client_id
                                    }
                                </a>
                                <Button size="auto" variant="normal">
                                    <InfoIcon />
                                </Button>
                            </div>
                        </Tooltip>
                        <ActionButton
                            onClick={() => {
                                copyToClipboard(
                                    clientPersonalInfo?.data?.data
                                        ?.merchant_client_id || ""
                                );
                                setIsIDCopied(!isIDCopied);
                                toast({
                                    mode: "success",
                                    message: "ID copied!",
                                    duration: 4000,
                                });
                            }}
                            size="auto"
                            variant="normal"
                            className="ml-2"
                            actionTitle="Copy ID"
                        >
                            <CopyIcon />
                        </ActionButton>
                    </div>
                </div>

                <div>
                    {!error &&
                        canAddCard &&
                        !(isSuccess && data.data.length === 0) && (
                            <Button
                                className="hidden sm:block"
                                variant="primary"
                                size="small"
                                type="button"
                                aria-label="add card button"
                                onClick={() => setDialogInView("add_card")}
                            >
                                Add card
                            </Button>
                        )}
                </div>
            </div>
            {cardIdsToDelete.length > 0 ? (
                <div className="mt-[--spacing-items]">
                    <Button
                        variant="danger"
                        onClick={() => setDialogInView("delete_cards")}
                    >
                        Delete cards
                    </Button>
                </div>
            ) : null}
        </div>
    );

    const handleViewTransactionHistory = (paymentId: string) => {
        setDialogInView("transaction_history");
        searchParams.set("payment_id", paymentId);
        setSearchParams(searchParams);
    };

    const handleCloseTransactionHistoryDialog = () => {
        setDialogInView(null);
        searchParams.delete("transaction_limit");
        searchParams.delete("transaction_page");
        searchParams.delete("payment_id");
        setSearchParams(searchParams);
    };

    const handleToggleCardIdsToDeleteList = (cardId: string) => {
        if (cardIdsToDelete.includes(cardId)) {
            setCardIdsToDelete(() =>
                cardIdsToDelete.filter((card) => card !== cardId)
            );
        } else setCardIdsToDelete(() => cardIdsToDelete.concat(cardId));
    };

    return (
        <>
            <AlertDialog open={dialogInView === "delete_cards"}>
                <AlertDialogContent
                    onSaveClick={handleDeleteCard}
                    saveText="Delete Cards"
                    onCancelClick={() => {
                        setCardIdsToDelete([]);
                        setDialogInView(null);
                    }}
                    title="Delete Cards"
                    description="Are you sure you want to delete cards? This action cannot be reversed"
                    isSaveBtnDisabled={deleteCards.isLoading}
                    isCancelBtnDisabled={deleteCards.isLoading}
                    isDeleting
                />
            </AlertDialog>
            <AlertDialog open={dialogInView === "delete_card"}>
                <AlertDialogContent
                    onSaveClick={handleDeleteCard}
                    saveText="Delete Card"
                    onCancelClick={() => {
                        setCardToDelete(null);
                        setDialogInView(null);
                    }}
                    title="Delete Card"
                    description="Are you sure you want to delete this card? This action cannot be reversed"
                    isSaveBtnDisabled={deleteCards.isLoading}
                    isCancelBtnDisabled={deleteCards.isLoading}
                    isDeleting
                />
            </AlertDialog>
            <Dialog open={dialogInView === "add_card"}>
                <DialogContent
                    variant="center"
                    saveText="Add card"
                    cancelText="Cancel"
                    title="Add card"
                    handleCloseDialog={() => setDialogInView(null)}
                    submitBtnFormValue="add-card"
                    isSubmitBtnDisabled={isMutating > 0}
                    isCancelBtnDisabled={isMutating > 0}
                >
                    <AddCardDetails
                        onFormSubmit={() => setDialogInView(null)}
                    />
                </DialogContent>
            </Dialog>

            <Dialog open={dialogInView === "add_label"}>
                <DialogContent
                    handleCloseDialog={() => setDialogInView(null)}
                    showFooter
                    title="Edit Label"
                    variant="center"
                    classNames="w-auto"
                    submitBtnFormValue="add-card-label"
                    wrapperClass="w-[572px]"
                    isSubmitBtnDisabled={isAddLabelMutating > 0}
                    isCancelBtnDisabled={isAddLabelMutating > 0}
                >
                    <AddCardLabelForm
                        onFormSubmit={() => setDialogInView(null)}
                        cardPaymentID={cardPaymentID}
                        cardLabel={defaultFormValue || ""}
                    />
                </DialogContent>
            </Dialog>
            <Dialog open={dialogInView === "transaction_history"}>
                <DialogContent
                    handleCloseDialog={handleCloseTransactionHistoryDialog}
                    showFooter={false}
                    classNames="w-auto"
                    wrapperClass="w-[1186px]"
                >
                    <CardTransactionHistory />
                </DialogContent>
            </Dialog>
            {isLoading && <Loader />}
            {error && error?.response?.status !== 404 && (
                <ListState
                    errorMsg={
                        error?.response?.data.message ||
                        `Cannot display client credit cards at this time please try again later`
                    }
                    stateHelperText="Try reloading this page to solve this issue"
                    isError
                    listHeader={listHeader}
                />
            )}
            {data && isSuccess && data.data.length === 0 && (
                <ListState
                    stateHelperText="Client credit cards will appear here when added"
                    isError={false}
                    listHeader={listHeader}
                    emptyMessage="No credit cards yet"
                    emptyBtnProps={{
                        showButton: canAddCard,
                        buttonText: "Add card",
                        onButtonClick: () => setDialogInView("add_card"),
                    }}
                />
            )}
            {isSuccess && data && data.data.length > 0 && (
                <Card>
                    <div>
                        <div className="[border-bottom:1px_solid_var(--color-stroke)] mb-[--spacing-header]">
                            {listHeader}
                        </div>
                        {data.data.map((card, index) => (
                            <>
                                <div
                                    className="flex pb-8 gap-x-2 justify-between items-center max-[1100px]:flex-col max-[1100px]:items-start gap-y-[--spacing-items]"
                                    key={`${index + 1}`}
                                >
                                    <div className="flex gap-[--spacing-items] min-[1100px]:items-center w-full min-[1100px]:w-auto flex-col items-start min-[1100px]:flex-row">
                                        {canManageCreditCards ? (
                                            <div>
                                                <FormCheckbox
                                                    onChange={() =>
                                                        handleToggleCardIdsToDeleteList(
                                                            card.payment_card_id
                                                        )
                                                    }
                                                    checked={cardIdsToDelete.includes(
                                                        card.payment_card_id
                                                    )}
                                                />
                                            </div>
                                        ) : null}
                                        <div className="flex bg-[--card-bg-gray] rounded-md px-[--spacing-items] py-[--padding-card-y] justify-between w-[300px] max-[1100px]:w-full">
                                            <div className="flex flex-col  justify-center gap-y-1">
                                                <p className="text-textSec text-caption font-semibold">
                                                    CARD TYPE
                                                    {card.is_current ? (
                                                        <Tooltip
                                                            content={
                                                                card.is_preferred
                                                                    ? "Set by staff"
                                                                    : "Chosen automatically"
                                                            }
                                                        >
                                                            <Button
                                                                variant="normal"
                                                                size="auto"
                                                            >
                                                                <Badge
                                                                    type="success"
                                                                    content="Current"
                                                                    className="ml-1"
                                                                />
                                                            </Button>
                                                        </Tooltip>
                                                    ) : null}
                                                </p>
                                                <div className="flex items-center">
                                                    <span>
                                                        {getCreditCardIconToDisplay(
                                                            card.card_type
                                                        )}
                                                    </span>
                                                    <span className="text-small font-semibold mx-2">
                                                        {card.card_type}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="flex flex-col  justify-center gap-y-1">
                                                <p className="text-textSec text-caption font-semibold">
                                                    CARD NUMBER
                                                </p>
                                                <span className="text-base font-medium">
                                                    {card.masked_card_number}
                                                </span>
                                            </div>
                                        </div>

                                        {card.is_card_expired && (
                                            <div>
                                                <Badge
                                                    type="danger"
                                                    content="Expired"
                                                />
                                            </div>
                                        )}
                                        <div className="flex flex-col  justify-center gap-y-1 text-caption">
                                            <p className="text-textSec text-caption font-semibold">
                                                ZIPCODE
                                            </p>
                                            <span className="text-base font-medium">
                                                {card.zip_code || "--"}
                                            </span>
                                        </div>
                                        <div className="flex flex-col  justify-center gap-y-1">
                                            <p className="text-textSec text-caption font-semibold">
                                                BILLING ADDRESS
                                            </p>
                                            <span className="text-base font-mediumm">
                                                {card.billing_address || "--"}
                                            </span>
                                        </div>

                                        <div className="bg-[--card-bg-gray] rounded-md w-[250px] px-[--spacing-items] py-[--padding-card-y]">
                                            <div className="mb-1 flex items-center gap-x-1">
                                                <p className="text-textSec text-caption font-semibold ">
                                                    LABEL
                                                </p>
                                                {canManageCreditCards ? (
                                                    <Button
                                                        variant="can-hover"
                                                        size="auto"
                                                        onClick={() => {
                                                            setDialogInView(
                                                                "add_label"
                                                            );
                                                            setCardPaymentID(
                                                                card.payment_card_id
                                                            );
                                                        }}
                                                    >
                                                        <EditIcon />
                                                    </Button>
                                                ) : null}
                                            </div>
                                            <span className="text-base font-medium">
                                                {card.card_label || "--"}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="min-[1100px]:ml-auto">
                                        {canManageCreditCards &&
                                        (card.card_status !==
                                            PaymentCardStatus.VALIDATED ||
                                            card.is_preferred ||
                                            !card.is_card_expired) ? (
                                            <Dropdown>
                                                <DropdownTrigger asChild>
                                                    <MoreButton />
                                                </DropdownTrigger>
                                                <DropdownContent
                                                    width="auto"
                                                    align="end"
                                                >
                                                    {card.card_status !==
                                                    PaymentCardStatus.VALIDATED ? (
                                                        <DropdownItem
                                                            onClick={() =>
                                                                handleCardToValidate(
                                                                    card.payment_card_id
                                                                )
                                                            }
                                                            disabled={
                                                                validateCard.isLoading
                                                            }
                                                        >
                                                            Validate
                                                        </DropdownItem>
                                                    ) : null}
                                                    {card.is_preferred ? (
                                                        <DropdownItem
                                                            onClick={() =>
                                                                handleSetPreferredCard(
                                                                    card.payment_card_id,
                                                                    false
                                                                )
                                                            }
                                                            disabled={
                                                                setPreferredCard.isLoading
                                                            }
                                                        >
                                                            Reset to automatic
                                                        </DropdownItem>
                                                    ) : null}
                                                    {!card.is_preferred &&
                                                    !card.is_card_expired ? (
                                                        <DropdownItem
                                                            onClick={() =>
                                                                handleSetPreferredCard(
                                                                    card.payment_card_id,
                                                                    true
                                                                )
                                                            }
                                                            disabled={
                                                                setPreferredCard.isLoading
                                                            }
                                                        >
                                                            Set as current
                                                        </DropdownItem>
                                                    ) : null}
                                                </DropdownContent>
                                            </Dropdown>
                                        ) : null}
                                    </div>
                                </div>
                                <div className="mb-[--spacing-items] pb-[--spacing-items] [border-bottom:1px_solid_var(--color-stroke)] border-b border-strokedark flex items-center gap-x-2 justify-between">
                                    <Button
                                        size="auto"
                                        variant="normal"
                                        className="font-medium text-caption text-textLink"
                                        onClick={() =>
                                            handleViewTransactionHistory(
                                                card.payment_card_id
                                            )
                                        }
                                    >
                                        View transaction history
                                    </Button>
                                    {canManageCreditCards ? (
                                        <ActionButton
                                            size="auto"
                                            variant="can-hover"
                                            aria-label="delete button"
                                            onClick={() => {
                                                setDialogInView("delete_card");
                                                setCardToDelete(
                                                    card.payment_card_id
                                                );
                                            }}
                                            actionTitle="Delete card"
                                        >
                                            <DeleteIcon />
                                        </ActionButton>
                                    ) : null}
                                </div>
                            </>
                        ))}
                    </div>
                </Card>
            )}
        </>
    );
}
